import './_request-select.scss';
import React, { useMemo, memo, useState } from 'react';
import Select, { components } from 'react-select';
import { List, AutoSizer, CellMeasurer, CellMeasurerCache } from 'react-virtualized';
import { isArray, isNull, isEqual } from 'lodash';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Spinner from '../../../Loaders/Spinner';

import { getSortedList } from '../helpers';

const DropdownIndicator = () => (
  <div
    style={{
      color: '#a0a0a0',
      height: 24,
      width: 32,
      justifyContent: 'center',
      display: 'flex',
      alignItems: 'center',
    }}
  >
    <span className="fa fa-search" />
  </div>
);

const ClearIndicator = (props) => (
  <components.ClearIndicator {...props} className="service-table__provider-dropdown-clear">
    <span className="fa fa-close" />
  </components.ClearIndicator>
);

const Option = ({ children, ...props }) => {
  // eslint-disable-next-line react/prop-types
  const { onMouseMove, onMouseOver, ...rest } = props.innerProps;
  const newProps = { ...props, innerProps: rest };

  return <components.Option {...newProps}>{children}</components.Option>;
};

const formatOptionLabel = (data) => (
  <div className={data.active === false ? 'not-active-service' : ''}>
    {data.id && data.withServicesId ? <span className="item-id">{data.id}</span> : null}
    <span>{data.label}</span>
    {' '}
    {data?.count !== null && data?.count >= 0 && (
      <span>
        (
        {data.count}
        )
      </span>
    )}
  </div>
);

const MenuList = ({ children, maxHeight }) => {
  const cache = new CellMeasurerCache({
    fixedWidth: true,
    minHeight: 20,
  });

  const rowCount = isArray(children) ? children.length : 0;
  if (isNull(children?.key)) {
    return <div>{children}</div>;
  }

  return (
    <AutoSizer disableHeight>
      {({ width }) => (
        <List
          deferredMeasurementCache={cache}
          width={width}
          overscanRowCount={0}
          height={maxHeight}
          rowHeight={cache.rowHeight}
          rowCount={rowCount}
          rowRenderer={({ index, key, parent, style }) => (
            <CellMeasurer key={key} cache={cache} parent={parent} columnIndex={0} rowIndex={index}>
              <div style={style} key={key}>
                {children[index]}
              </div>
            </CellMeasurer>
          )}
        />
      )}
    </AutoSizer>
  );
};

const isEmptyCounts = (options) => {
  if (options && options.length) {
    if (!isNull(options[0].count)) {
      return false;
    }
  }
  return true;
};

const Control = (props) => {
  // eslint-disable-next-line react/prop-types
  const {
    selectProps: { isSorted, sortMode, onChangeSortMode, isSearchable },
    options,
  } = props;
  const isEmptyCount = isEmptyCounts(options);
  if (isSorted) {
    return (
      <div className="dropdown-select__sort">
        <div className="dropdown-select__sort-control">
          <components.Control {...props} />
        </div>
        <div
          className={classNames('dropdown-select__sort-icon', {
            'dropdown-select__sort-icon-disable': !sortMode,
          })}
        >
          {isEmptyCount ? <Spinner /> : <span className="fas fa-sort-amount-down" onClick={onChangeSortMode} />}
        </div>
      </div>
    );
  }
  if (!isSearchable) return null;
  return <components.Control {...props} />;
};

const VirtualizedSelect = ({ darkMode, items, isSorted, onChangeSelected, value, withServicesId, selectSearch, useDefaultValue }) => {
  /*
    Сортировка работат только в одну сторону (По убыванию)
    Задача: https://users.scrummate.com/#story/1723256/PNL-6989-%D0%94%D0%BE%D0%B1%D0%B0%D0%B2%D0%BB%D0%B5%D0%BD%D0%B8%D0%B5-%D1%81%D0%BE%D1%80%D1%82%D0%B8%D1%80%D0%BE%D0%B2%D0%BA%D0%B8-%D0%B2-%D1%84%D0%B8%D0%BB%D1%8C%D1%82%D1%80%D0%B5-%D0%BF%D0%BE-Service-%D0%B2-Orders
   */
  const [sortMode, setSortMode] = useState(null);
  const onChangeSortMode = () => {
    const mode = sortMode ? null : 'DESC';
    setSortMode(mode);
  };
  const options = useMemo(() => getSortedList(items, sortMode), [items, sortMode]);

  const selectComponents = {
    DropdownIndicator,
    ClearIndicator,
    Option,
    Control,
    IndicatorSeparator: null,
  };

  const selectComponentsVirtual = { ...selectComponents, MenuList };
  const selectValue = useDefaultValue ? { defaultValue: value } : { value };

  const filterOptions = (candidate, input) => {
    if (candidate.data.__isNew__) return true;
    return candidate?.label?.includes(input) || String(candidate?.value)?.includes(input);
  };

  return (
    <Select
      captureMenuScroll={false}
      // filterOption={createFilter({ ignoreAccents: false })}
      filterOption={filterOptions}
      components={items?.length > 100 ? selectComponentsVirtual : selectComponents}
      options={options}
      onChange={onChangeSelected}
      isSearchable={selectSearch}
      isMulti
      autoFocus
      menuIsOpen
      noOptionsMessage={() => 'No options'}
      controlShouldRenderValue={false}
      hideSelectedOptions={false}
      isSorted={isSorted}
      sortMode={sortMode}
      onChangeSortMode={onChangeSortMode}
      styles={{
        container: (styles) => ({
          ...styles,
          width: selectSearch ? '410px' : '190px',
          background: darkMode ? '#222327' : '#ffffff',
        }),
        valueContainer: (styles) => ({
          ...styles,
          paddingTop: '2px',
          paddingBottom: '2px',
          paddingLeft: '0px',
          paddingRight: '28px',
        }),
        menu: (styles) => ({
          ...styles,
          marginTop: '4px',
          boxShadow: 'none',
          border: 'none',
          borderTopLeftRadius: '0px',
          borderTopRightRadius: '0px',
          position: 'relative',
          background: darkMode ? '#222327' : '#ffffff',
        }),
        input: (styles) => ({
          ...styles,
          color: darkMode ? '#dfe0e1' : '#333',
        }),
        control: (styles) => ({
          ...styles,
          minHeight: '34px',
          fontWeight: 'normal',
          marginLeft: '7px',
          marginRight: '7px',
          flexDirection: 'row-reverse',
          borderBottomWidth: '1px',
          border: darkMode ? 'inset 0 2px 4px 0 rgb(0 0 0 / 20%)' : '1px solid #ddd',
          boxShadow: 'inset 0 1px 1px rgba(0,0,0,.075)',
          cursor: 'text',
          color: darkMode ? '#dfe0e1' : '#333',
          background: darkMode ? '#2e3036' : '#ffffff',
          borderColor: 'rgba(103, 107, 118, 0.5)',
          '&:hover': {
            border: darkMode ? 'solid 1px rgba(103, 107, 118, 0.5)' : '1px solid #ccc',
          },
        }),
        option: (styles, { isSelected }) => ({
          ...styles,
          whiteSpace: 'normal',
          fontWeight: 'normal',
          paddingLeft: '10px',
          paddingRight: '32px',
          paddingTop: '3px',
          paddingBottom: '3px',
          cursor: 'pointer',
          color: darkMode ? '#dfe0e1' : '#333',
          background: darkMode ? '#222327' : '#ffffff',
          '&:hover': {
            background: darkMode ? '#2e3036' : '#f5f5f5',
          },
          '&:active': {
            background: darkMode ? '#2e3036' : '#f5f5f5',
          },
          '&:before': isSelected
            ? {
              content: '"\\e013"',
              fontFamily: 'Glyphicons Halflings',
              fontStyle: 'normal',
              fontWeight: 400,
              position: 'absolute',
              right: '15px',
            }
            : null,
        }),
      }}
      formatOptionLabel={(data) => formatOptionLabel({ ...data, withServicesId })}
      getOptionValue={(option) => option.id}
      placeholder="Search"
      {...selectValue}
    />
  );
};

Option.propTypes = {
  children: PropTypes.any,
};

Control.propTypes = {
  selectProps: PropTypes.object,
  options: PropTypes.array,
};

MenuList.propTypes = {
  children: PropTypes.any,
  maxHeight: PropTypes.number,
};

VirtualizedSelect.propTypes = {
  darkMode: PropTypes.number,
  items: PropTypes.array,
  onChangeSelected: PropTypes.func,
  value: PropTypes.any,
  isSorted: PropTypes.bool,
  withServicesId: PropTypes.bool,
  selectSearch: PropTypes.bool,
  useDefaultValue: PropTypes.bool,
};

VirtualizedSelect.defaultProps = {
  items: [],
  isSorted: false,
  withServicesId: true,
  selectSearch: true,
};

const arePropsEqual = (prevProps, nextProps) => isEqual([prevProps.darkMode, prevProps.items, prevProps.value], [nextProps.darkMode, nextProps.items, nextProps.value]);
export default memo(VirtualizedSelect, arePropsEqual);
